import { useEffect, useRef, useState } from 'react';
import { useSWRConfig } from 'swr';
import { EngineService } from '@/services/engine';
import { ScoredEvent } from '@/types/event';
import { VehicleSearchResult } from '@/types/vehicle';
import { PersonSearchResult } from '@/types/person';
import { PromptAttachmentType } from '@/types/prompt';
import { isValidEvent } from '@/utils/query-filters';
import {
    AnalyzedQueryInfo,
    SimilarResult,
    SuggestedQuery,
    useQueryResultsStore,
} from '@/stores/query-results.store';
import { QueryStreamMessageType } from '@/types/stream';
import camelcaseKeys from 'camelcase-keys';

export interface QueryParams {
    text: string;
    sourceIds: string[];
    excludedEventIds?: string[];
    filters?: {
        startTime?: Date;
        endTime?: Date;
    };
    attachment?: {
        url: string;
        type?: PromptAttachmentType;
    };
    classIds?: number[];
    demo?: boolean;
    topK?: number;
    minConfidence?: number;
    sceneOnly?: boolean;
    simulation?: boolean;
}

function buildStableCacheKey(params: QueryParams): string {
    const keyObj = {
        attachment: params.attachment
            ? {
                  url: params.attachment.url,
                  ...(params.attachment.type && {
                      type: params.attachment.type,
                  }),
              }
            : undefined,
        filters: params.filters
            ? {
                  endTime: params.filters.endTime?.toISOString(),
                  startTime: params.filters.startTime?.toISOString(),
              }
            : undefined,
        sourceIds: [...params.sourceIds].sort(),
        text: params.text,
        classIds: params.classIds ? [...params.classIds].sort() : undefined,
        minConfidence: params.minConfidence,
        sceneOnly: params.sceneOnly,
    };

    return `query:${JSON.stringify(keyObj)}`;
}

export interface StreamMeta {
    cursor?: string;
    summary?: string[];
}

interface StreamChunkCallbacks {
    onEventReceived?: (event: ScoredEvent) => void;
    onMeta?: (meta: StreamMeta) => void;
    onAnalyzedQueries?: (queries: AnalyzedQueryInfo[]) => void;
    onVehicleResults?: (results: VehicleSearchResult[]) => void;
    onPersonResults?: (results: PersonSearchResult[]) => void;
    onSuggestion?: (suggestions: SuggestedQuery[]) => void;
    onSimilarResults?: (results: SimilarResult[]) => void;
    onError?: (message: string) => void;
}

export interface ExecuteOptions {
    onEventReceived?: (event: ScoredEvent) => void;
    onMeta?: (meta: StreamMeta) => void;
    onAnalyzedQueries?: (queries: AnalyzedQueryInfo[]) => void;
    onVehicleResults?: (results: VehicleSearchResult[]) => void;
    onPersonResults?: (results: PersonSearchResult[]) => void;
    onSuggestion?: (suggestions: SuggestedQuery[]) => void;
    onSimilarResults?: (results: SimilarResult[]) => void;
    onError?: (message: string) => void;
    isLoadingMore?: boolean;
    skipCache?: boolean;
}

export interface ContinueOptions {
    onEventReceived?: (event: ScoredEvent) => void;
    onMeta?: (meta: StreamMeta) => void;
    onAnalyzedQueries?: (queries: AnalyzedQueryInfo[]) => void;
    onError?: (message: string) => void;
}

function createStreamChunkHandler(
    events: ScoredEvent[],
    callbacks: StreamChunkCallbacks,
    setProgress: (updater: (prev: number) => number) => void,
    isCurrentRun: () => boolean,
    cacheKey?: string,
    mutate?: (
        key: string,
        updater: (prev?: ScoredEvent[]) => ScoredEvent[],
        options: { revalidate: boolean },
    ) => void,
) {
    return (chunk: string) => {
        if (!isCurrentRun()) return;
        try {
            const message = JSON.parse(chunk);
            const { type, data } = message;

            switch (type) {
                case QueryStreamMessageType.AnalyzedQueries: {
                    callbacks.onAnalyzedQueries?.(
                        camelcaseKeys(data, { deep: true }),
                    );
                    break;
                }
                case QueryStreamMessageType.Vehicles: {
                    callbacks.onVehicleResults?.(
                        camelcaseKeys(data, {
                            deep: true,
                        }) as VehicleSearchResult[],
                    );
                    break;
                }
                case QueryStreamMessageType.Persons: {
                    callbacks.onPersonResults?.(
                        camelcaseKeys(data, {
                            deep: true,
                        }) as PersonSearchResult[],
                    );
                    break;
                }
                case QueryStreamMessageType.Event: {
                    const parsed = camelcaseKeys(data, { deep: true });
                    const confidence = parsed?.confidence;
                    const focuses = parsed?.focuses ?? [];

                    if (!isValidEvent(parsed)) {
                        console.warn(
                            'Received invalid event structure:',
                            parsed,
                        );
                        return;
                    }

                    const scored: ScoredEvent = {
                        event: { ...parsed },
                        confidence,
                        scores: {},
                        focuses,
                    };
                    events.push(scored);
                    setProgress((prev) => prev + 1);
                    callbacks.onEventReceived?.(scored);

                    if (cacheKey && mutate) {
                        mutate(
                            cacheKey,
                            (prev: ScoredEvent[] = []) => [...prev, scored],
                            { revalidate: false },
                        );
                    }
                    break;
                }
                case QueryStreamMessageType.Meta: {
                    const suggestions: SuggestedQuery[] = (
                        data?.suggestions ?? []
                    ).map(
                        (item: { text: string; use_attachment?: boolean }) => ({
                            text: item.text,
                            useAttachment: !!item.use_attachment,
                        }),
                    );
                    callbacks.onSuggestion?.(suggestions);

                    const similarResults: SimilarResult[] = (
                        data?.similar_results ?? []
                    ).map((raw: Record<string, unknown>) => {
                        const parsed = camelcaseKeys(raw, { deep: true });
                        return {
                            scoredEvent: {
                                event: { ...parsed },
                                confidence: parsed?.confidence,
                                scores: {},
                                focuses: parsed?.focuses ?? [],
                            },
                            description: parsed?.description ?? '',
                        };
                    });
                    callbacks.onSimilarResults?.(similarResults);

                    callbacks.onMeta?.(data);
                    break;
                }
                case QueryStreamMessageType.Error: {
                    console.error('Query stream error:', data?.error);
                    callbacks.onError?.(data?.code || 'unknown');
                    break;
                }
                default: {
                    console.warn('Unknown stream message type:', type, message);
                }
            }
        } catch (e) {
            console.error('Failed to parse stream chunk:', e);
        }
    };
}

let activeRunId = 0;
let activeAbort: AbortController | null = null;

interface StreamContext {
    signal: AbortSignal;
    onChunk: (chunk: string) => void;
    onSettled: () => void;
}

async function runStream(
    callbacks: StreamChunkCallbacks,
    trackLoading: boolean,
    invoke: (context: StreamContext) => Promise<void>,
): Promise<ScoredEvent[]> {
    const { setQueryLoading } = useQueryResultsStore.getState();
    activeAbort?.abort();
    const abort = new AbortController();
    activeAbort = abort;
    const runId = ++activeRunId;
    const isCurrentRun = () => activeRunId === runId;
    const isLiveChunk = () => isCurrentRun() && !abort.signal.aborted;

    if (trackLoading) setQueryLoading(true);
    const settle = () => {
        if (trackLoading && isCurrentRun()) setQueryLoading(false);
    };

    const events: ScoredEvent[] = [];

    try {
        await invoke({
            signal: abort.signal,
            onChunk: createStreamChunkHandler(
                events,
                callbacks,
                () => {},
                isLiveChunk,
            ),
            onSettled: settle,
        });
        return events;
    } catch (err) {
        settle();
        if (!isCurrentRun() || abort.signal.aborted) {
            return events;
        }
        throw err;
    }
}

export async function execute(
    params: QueryParams,
    options: ExecuteOptions = {},
): Promise<ScoredEvent[]> {
    const {
        onEventReceived,
        onMeta,
        onAnalyzedQueries = () => {},
        onVehicleResults = () => {},
        onPersonResults = () => {},
        onSuggestion = () => {},
        onSimilarResults = () => {},
        onError,
    } = options;

    return runStream(
        {
            onEventReceived,
            onMeta,
            onAnalyzedQueries,
            onVehicleResults,
            onPersonResults,
            onSuggestion,
            onSimilarResults,
            onError,
        },
        true,
        ({ signal, onChunk, onSettled }) =>
            EngineService.query({
                text: params.text,
                sourceIds: params.sourceIds,
                excludedEventIds: params.excludedEventIds ?? [],
                attachment: params.attachment,
                startTime: params.filters?.startTime,
                endTime: params.filters?.endTime,
                classIds: params.classIds,
                demo: params.demo ?? true,
                topK: params.topK,
                minConfidence: params.minConfidence,
                sceneOnly: params.sceneOnly,
                simulation: params.simulation,
                signal,
                streamCallbacks: {
                    onStart: () => {},
                    onChunk,
                    onDone: onSettled,
                    onEmptyResponse: onSettled,
                },
            }),
    );
}

export async function executeContinue(
    cursor: string,
    topK: number,
    options: ContinueOptions = {},
): Promise<ScoredEvent[]> {
    const { onEventReceived, onMeta, onAnalyzedQueries, onError } = options;

    return runStream(
        { onEventReceived, onMeta, onAnalyzedQueries, onError },
        false,
        ({ signal, onChunk, onSettled }) =>
            EngineService.queryContinue({
                cursor,
                topK,
                signal,
                streamCallbacks: {
                    onStart: () => {},
                    onChunk,
                    onDone: onSettled,
                    onEmptyResponse: onSettled,
                },
            }),
    );
}

export function useQuery() {
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<Error | null>(null);
    const { cache, mutate } = useSWRConfig();
    const abortControllerRef = useRef<AbortController | null>(null);
    const activeRunIdRef = useRef(0);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const cancel = () => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
        setError(null);
    };

    const execute = async (
        params: QueryParams,
        options: ExecuteOptions = {},
    ): Promise<ScoredEvent[]> => {
        const {
            onEventReceived,
            onMeta,
            onAnalyzedQueries = () => {},
            onVehicleResults = () => {},
            onPersonResults = () => {},
            onSuggestion = () => {},
            onSimilarResults = () => {},
            onError,
            isLoadingMore = false,
            skipCache = false,
        } = options;
        const cacheKey = buildStableCacheKey(params);

        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const runId = ++activeRunIdRef.current;
        const isCurrentRun = () => activeRunIdRef.current === runId;
        const isLiveChunk = () =>
            isCurrentRun() && !abortController.signal.aborted;

        setIsLoading(true);
        if (!isLoadingMore) {
            setProgress(0);
        }
        setError(null);

        const events: ScoredEvent[] = [];

        if (!skipCache) {
            const cachedEvents = cache.get(cacheKey)?.data as
                | ScoredEvent[]
                | undefined;
            if (cachedEvents && cachedEvents.length > 0 && !isLoadingMore) {
                cachedEvents.forEach((event) => {
                    onEventReceived?.(event);
                });
                setProgress(cachedEvents.length);
                events.push(...cachedEvents);
                setIsLoading(false);
                return events;
            }
        }

        if (!isLoadingMore) {
            mutate(cacheKey, [], { revalidate: false, populateCache: true });
        }

        try {
            await EngineService.query({
                text: params.text,
                sourceIds: params.sourceIds,
                excludedEventIds: params.excludedEventIds ?? [],
                attachment: params.attachment,
                startTime: params.filters?.startTime,
                endTime: params.filters?.endTime,
                classIds: params.classIds,
                demo: params.demo ?? true,
                topK: params.topK,
                minConfidence: params.minConfidence,
                sceneOnly: params.sceneOnly,
                simulation: params.simulation,
                signal: abortController.signal,
                streamCallbacks: {
                    onStart: () => {},
                    onChunk: createStreamChunkHandler(
                        events,
                        {
                            onEventReceived,
                            onMeta,
                            onAnalyzedQueries,
                            onVehicleResults,
                            onPersonResults,
                            onSuggestion,
                            onSimilarResults,
                            onError,
                        },
                        setProgress,
                        isLiveChunk,
                        isLoadingMore ? undefined : cacheKey,
                        isLoadingMore ? undefined : mutate,
                    ),
                    onDone: () => {
                        if (isCurrentRun()) setIsLoading(false);
                    },
                    onEmptyResponse: () => {
                        if (isCurrentRun()) setIsLoading(false);
                    },
                },
            });

            return events;
        } catch (err) {
            // A newer run superseded this one; it now owns the loading state,
            // so leave it untouched.
            if (!isCurrentRun()) {
                return events;
            }
            // This run was cancelled (e.g. stopSimulation) and nothing will
            // supersede it, so clear the loading state here.
            if (abortController.signal.aborted) {
                setIsLoading(false);
                return events;
            }
            const error =
                err instanceof Error
                    ? err
                    : new Error('Query execution failed');
            setError(error);
            setIsLoading(false);
            throw error;
        }
    };

    return {
        execute,
        cancel,
        isLoading,
        progress,
        error,
    };
}
